import { HelcimClient, VerifyCardCheckoutReq } from '@kalos/kalos-rpc';

export type CreateHelcimVerifyCheckoutSessionResult =
  | { ok: true; checkoutToken: string }
  | { ok: false; error: string };

function formatClientError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function verifyCardReq(userId: number): VerifyCardCheckoutReq {
  return VerifyCardCheckoutReq.create({
    userId,
    paymentMethod: 'cc',
    currency: 'USD',
    digitalWallet: '',
    confirmationScreen: false,
  });
}

/**
 * Tries CreateVerifyCardCheckoutSession several times (API occasionally returns an empty token first).
 */
async function tryVerifyCardCheckoutToken(
  client: HelcimClient,
  userId: number,
  attempts: number,
  delaysMs: readonly number[],
): Promise<{ ok: true; checkoutToken: string } | { ok: false; error: string }> {
  const req = verifyCardReq(userId);
  let lastError = 'No checkout token received from Helcim';
  for (let i = 0; i < attempts; i++) {
    try {
      const response = await client.createVerifyCardCheckoutSession(req);
      const token = response?.checkoutToken?.trim();
      if (token) {
        return { ok: true, checkoutToken: token };
      }
      lastError = 'No checkout token received from Helcim';
    } catch (err) {
      lastError = formatClientError(err);
    }
    if (i < attempts - 1) {
      await sleep(delaysMs[i] ?? 2000);
    }
  }
  return { ok: false, error: lastError };
}

/**
 * Helcim Pay.js session for card verification / tokenize-on-file via CreateVerifyCardCheckoutSession
 * (no invoice required).
 */
export async function createHelcimVerifyCheckoutSession(params: {
  apiBaseUrl: string;
  userId: number;
}): Promise<CreateHelcimVerifyCheckoutSessionResult> {
  const { apiBaseUrl, userId } = params;
  const client = new HelcimClient(apiBaseUrl);
  const delays = [600, 1200, 2000, 2800] as const;
  return tryVerifyCardCheckoutToken(client, userId, delays.length + 1, delays);
}
